import React from 'react';
import '@date-io/luxon'; // This is only needed to satisfy types on build
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers';
import { useTranslation } from 'react-i18next';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  StyledTextField,
  PaperStyles,
  InputAdornmentStyles,
  DateRangeTextField,
  DateRangeFieldSelector,
  DateRangeLayout,
  MobilePaperStyles,
  DateRangePickerFieldStyles,
} from './styles';
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  Calendar,
  Lock,
  Close,
} from '@octanner/prism-icons';
import { DateRange } from '@mui/x-date-pickers-pro';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { LocalizationProvider as DateRangeLocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import moment, { Moment } from 'moment';
import { Props } from './types';

const DatePicker = <TInputDate, TDate = TInputDate>({
  dateAdapter,
  dateFormats,
  dateLibInstance,
  localeText,
  adapterLocale,
  locale,
  renderInput,
  isDateRange = false,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  calendars = 1,
  minDate,
  maxDate,
  disableDateRange,
  readOnlyDateRange,
  ...props
}: Props<TInputDate, TDate>) => {
  const { t } = useTranslation();

  if (isDateRange) {
    const [dateValue, setdateValue] = React.useState<DateRange<Moment>>([
      startDate ? moment(startDate).startOf('day') : null,
      endDate ? moment(endDate).startOf('day') : null,
    ]);

    const handleDateRangeChange = (value: DateRange<Moment>) => {
      setdateValue(value);
      onStartDateChange?.(value[0]);
      onEndDateChange?.(value[1]);
    };

    const handleClearStart = () => {
      setdateValue([null, dateValue[1]]);
      onStartDateChange?.(null);
    };

    const handleClearEnd = () => {
      setdateValue([dateValue[0], null]);
      onEndDateChange?.(null);
    };

    return (
      <DateRangeLocalizationProvider dateAdapter={AdapterMoment}>
        <DateRangePicker
          calendars={calendars}
          value={dateValue}
          onChange={handleDateRangeChange}
          localeText={{
            start: t('prism-header:dateRangePicker-startDate', 'Start date'),
            end: t('prism-header:dateRangePicker-endDate', 'End date'),
          }}
          minDate={minDate && moment(minDate).startOf('day')}
          maxDate={maxDate && moment(maxDate).startOf('day')}
          slotProps={{
            field: DateRangePickerFieldStyles,
            mobilePaper: {
              sx: MobilePaperStyles,
            },
            textField: ({ position }) => {
              const isDateVisible =
                position === 'start' ? dateValue[0] : dateValue[1];
              const isClearable =
                !readOnlyDateRange && !disableDateRange && isDateVisible;
              const handleClearDate =
                position === 'start' ? handleClearStart : handleClearEnd;
              return {
                InputProps: {
                  endAdornment: readOnlyDateRange ? (
                    <Lock />
                  ) : (
                    <div className="calendarEndIcons">
                      {isClearable && (
                        <Close
                          onClick={handleClearDate}
                          className="closeIcon"
                        />
                      )}
                      <Calendar />
                    </div>
                  ),
                },
                sx: DateRangeTextField,
              };
            },
            fieldSeparator: {
              sx: DateRangeFieldSelector,
            },
            layout: {
              sx: DateRangeLayout,
            },
          }}
          readOnly={readOnlyDateRange}
          disabled={disableDateRange}
        />
      </DateRangeLocalizationProvider>
    );
  }

  return (
    <LocalizationProvider
      dateAdapter={dateAdapter}
      adapterLocale={adapterLocale || locale || 'en-US'}
      dateFormats={dateFormats}
      dateLibInstance={dateLibInstance}
      localeText={localeText}
    >
      <MuiDatePicker
        inputFormat="dd MMM yyyy"
        renderInput={
          renderInput ||
          ((params) => (
            <StyledTextField
              {...params}
              InputProps={{
                ...params.InputProps,
                readOnly: props.readOnly,
              }}
              inputProps={{
                ...params.inputProps,
                placeholder: t(
                  'prism-header:datepicker-placeholder',
                  'DD MMM YYYY',
                ),
              }}
              helperText={t(
                'prism-header:datepicker-helper-text',
                'Enter date in DD MMM YYYY format',
              )}
            />
          ))
        }
        components={{
          SwitchViewIcon: ArrowDown,
          LeftArrowIcon: ArrowLeft,
          RightArrowIcon: ArrowRight,
        }}
        InputAdornmentProps={{
          sx: InputAdornmentStyles,
        }}
        PaperProps={{
          sx: PaperStyles,
        }}
        DialogProps={{
          sx: PaperStyles,
        }}
        {...props}
      />
    </LocalizationProvider>
  );
};

export default DatePicker;
